

import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon } from './icons/PlaybackIcons';
import { userService } from '../services/userService';
import type { UserProfile } from '../types';

interface AthletePerformance {
  id: string;
  name: string;
  color: string;
  rank: number;
  startTime?: number;
  startCoordinate?: { lat: number; lon: number };
  userId?: string;
  currentMetrics: {
    speed: number; // in km/h
    power: number | null;
    heartRate: number | null;
  };
}

interface AthleteProfileProps {
  athlete: AthletePerformance;
  onBack: () => void;
}

const MetricDisplay: React.FC<{ value: string; unit: string; label: string; }> = ({ value, unit, label }) => (
    <div className="flex flex-col items-center bg-gray-700/50 rounded-lg p-3 w-full">
        <span className="text-xs text-gray-400 uppercase">{label}</span>
        <span className="font-bold text-2xl text-gray-100">{value}</span>
        <span className="text-sm text-gray-400">{unit}</span>
    </div>
);

const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex justify-between items-center py-1 border-b border-gray-600 last:border-0">
        <span className="text-gray-400 text-sm">{label}</span>
        <span className="text-white font-medium text-sm">{value}</span>
    </div>
);

const AthleteProfile: React.FC<AthleteProfileProps> = ({ athlete, onBack }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
      const fetchProfile = async () => {
          const targetUserId = athlete.userId || athlete.id;
          if (targetUserId) {
              try {
                  const profile = await userService.getUserProfile(targetUserId);
                  setUserProfile(profile);
              } catch (error) {
                  console.error("Failed to fetch linked user profile", error);
              }
          } else {
              setUserProfile(null);
          }
      }
      fetchProfile();
  }, [athlete.userId, athlete.id]);

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <button onClick={onBack} className="flex items-center text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to All Athletes
        </button>
      </div>

      <div className="text-center mb-4">
        <img
          src={userProfile?.photoURL || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(athlete.name)}&backgroundColor=${(athlete.color || '#34d399').substring(1)}&size=128`}
          alt={`${athlete.name}'s profile picture`}
          className="w-32 h-32 rounded-full mx-auto border-4 object-cover"
          style={{ borderColor: athlete.color || '#34d399' }}
        />
        <h2 className="mt-3 text-2xl font-bold text-white">{userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : athlete.name}</h2>
        <div className="mt-1 text-lg font-semibold text-gray-300">
            Position: <span className="text-cyan-400">{athlete.rank}</span>
        </div>
        
        <div className="flex flex-col gap-1 mt-2">
            {userProfile?.teamName && (
                <div className="text-sm font-medium text-cyan-400">
                    {userProfile.teamName}
                </div>
            )}
            {athlete.startTime && athlete.startTime > 0 && (
                <div className="text-xs text-gray-400 bg-gray-700/30 inline-block px-2 py-1 rounded mx-auto mt-1">
                    File Start: {new Date(athlete.startTime).toLocaleTimeString()}
                </div>
            )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <MetricDisplay 
            label="Speed"
            value={(athlete.currentMetrics.speed * 0.621371).toFixed(1)} 
            unit="mph" 
        />
        <MetricDisplay 
            label="Power"
            value={athlete.currentMetrics.power !== null ? Math.round(athlete.currentMetrics.power).toString() : '—'} 
            unit="Watts" 
        />
        <MetricDisplay 
            label="Heart Rate"
            value={athlete.currentMetrics.heartRate !== null ? Math.round(athlete.currentMetrics.heartRate).toString() : '—'} 
            unit="Bpm" 
        />
      </div>

      <div className="space-y-4 text-sm flex-grow overflow-y-auto pr-2">
        {userProfile ? (
            <div>
                <h4 className="font-semibold text-cyan-400 mb-2">Rider Details</h4>
                <div className="bg-gray-700/50 p-3 rounded-md">
                    {userProfile.age !== undefined && userProfile.age > 0 && (
                        <StatRow label="Age" value={userProfile.age.toString()} />
                    )}
                    <StatRow label="Sex" value={userProfile.sex || 'N/A'} />
                    {((userProfile.heightFeet !== undefined && userProfile.heightFeet > 0) || 
                      (userProfile.heightInches !== undefined && userProfile.heightInches > 0)) && (
                        <StatRow label="Height" value={`${userProfile.heightFeet || 0}' ${userProfile.heightInches || 0}"`} />
                    )}
                    {userProfile.ftp !== undefined && userProfile.ftp > 0 && (
                        <StatRow label="FTP" value={`${userProfile.ftp} W`} />
                    )}
                    {userProfile.weightLbs !== undefined && userProfile.weightLbs > 0 && (
                        <StatRow label="Weight" value={`${userProfile.weightLbs} lbs`} />
                    )}
                    {userProfile.bikeWeightLbs !== undefined && userProfile.bikeWeightLbs > 0 && (
                        <StatRow label="Bike Weight" value={`${userProfile.bikeWeightLbs} lbs`} />
                    )}
                    {userProfile.usacLicenseNumber && (
                        <StatRow label="USAC License" value={userProfile.usacLicenseNumber} />
                    )}
                </div>
            </div>
        ) : (
            <div>
                <h4 className="font-semibold text-cyan-400 mb-1">Athlete Bio</h4>
                <p className="text-gray-300 bg-gray-700/50 p-3 rounded-md">
                    A seasoned climber from the Pacific Northwest, {athlete.name} is known for explosive attacks on steep gradients and consistent high-wattage efforts.
                </p>
            </div>
        )}
      </div>
    </div>
  );
};

export default AthleteProfile;